/**
 * Chart design tokens.
 *
 * Both modes are selected, not derived - the dark values are the same hues
 * re-stepped for the dark surface. The categorical slots below were validated
 * against this app's actual MUI surfaces (#ffffff light, #121212 dark) rather
 * than assumed:
 *
 *   light: worst adjacent CVD dE 9.1, normal-vision 22.9  -> PASS
 *   dark:  worst adjacent CVD dE 8.4, normal-vision 19.8  -> PASS
 *
 * Light-mode aqua and yellow fall below 3:1 against white, so the relief rule
 * applies: every chart using them ships visible direct labels AND a table view.
 * Do not add a 5th platform colour without re-running the validator.
 */

// Categorical slots, in fixed order. Never cycle past the end of this list.
const CATEGORICAL_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const CATEGORICAL_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500'];

/**
 * Platform -> slot index, pinned by entity rather than by rank.
 *
 * This is the whole point: if a filter drops Swiggy, Zomato must stay blue. A
 * reader who learned "Zomato is blue" is misled the moment colours reshuffle.
 */
const PLATFORM_SLOT = {
    Zomato: 0,
    Swiggy: 1,
    'Ownly Food': 2,
    Olafood: 3,
    // UberEats is deliberately absent: only four slots have been validated
    // against these surfaces, and a fifth would need re-running the validator
    // rather than inventing a hue. It falls back to muted until then.
};

export function getVizTokens(isDark) {
    const categorical = isDark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
    return {
        categorical,
        // Single-series marks (trend, magnitude bars) use slot 1.
        series: categorical[0],
        // Nominal magnitude bars all take one colour - never a value-ramp, which
        // would double-encode bar length as hue.
        magnitude: categorical[0],
        surface: isDark ? '#121212' : '#ffffff',
        grid: isDark ? '#2c2c2a' : '#e1e0d9',
        axis: isDark ? '#383835' : '#c3c2b7',
        textPrimary: isDark ? '#ffffff' : '#0b0b0b',
        textSecondary: isDark ? '#c3c2b7' : '#52514e',
        textMuted: '#898781',
        good: isDark ? '#0ca30c' : '#006300',
        critical: isDark ? '#e66767' : '#d03b3b',
        platformColor(platform) {
            const slot = PLATFORM_SLOT[platform];
            // Unknown platforms fall back to muted rather than inventing a hue -
            // a generated 9th colour is indistinguishable under CVD.
            return slot === undefined ? this.textMuted : categorical[slot];
        },
    };
}

export { PLATFORM_SLOT };
